import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { MAIN_SITE_URL } from '@/mocks/site';
import { categories } from '@/mocks/categories';

const navLinks = [
  { to: '/', key: 'nav.home', end: true },
  { to: '/article/usfans-spreadsheet-guide', key: 'nav.guides' },
  { to: '/article/usfans-buying-guide', key: 'nav.buyingGuide' },
  { to: '/topics/review', key: 'nav.review' },
  { to: '/topics/shipping-cost', key: 'nav.shipping' },
  { to: '/topics/safety', key: 'nav.safety' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  const lang = (i18n.language || 'en').split('-')[0];
  const getName = (name: { en: string; pl: string; es: string; fr: string }) =>
    name[lang as 'en' | 'pl' | 'es' | 'fr'] || name.en;

  return (
    <header className="sticky top-0 z-40 border-b-2 border-background-300 bg-background-50">
      <div className="mx-auto flex h-16 items-center justify-between px-4 md:px-6 lg:px-8">
        <Link
          to="/"
          className="whitespace-nowrap font-heading text-xl font-extrabold tracking-tight text-foreground-950"
        >
          <span className="text-primary-600">USFans</span> Spreadsheet
        </Link>

        <nav className="hidden items-center gap-5 md:flex">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `whitespace-nowrap text-sm font-semibold transition-colors ${
                  isActive ? 'text-primary-600' : 'text-foreground-800 hover:text-primary-600'
                }`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            {t('nav.buyNow')}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-900 md:hidden"
          aria-label="Menu"
        >
          <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {menuOpen && (
        <div className="max-h-[75vh] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? 'bg-primary-50 text-primary-600' : 'text-foreground-800 hover:bg-background-100'
                  }`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
          </nav>

          <div className="mt-4 border-t border-background-200 pt-4">
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
              {t('home.categoriesTitle')}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1">
              {categories.map((c) => (
                <a
                  key={c.slug}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                >
                  <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center text-primary-500">
                    <i className="ri-arrow-right-up-line text-base"></i>
                  </span>
                  <span className="truncate">{getName(c.name)}</span>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-4">
            <LanguageSwitcher />
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-center text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              {t('nav.buyNow')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}